import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { awsConfig } from "../aws-config";
import { getAuthHeader, getUploadUrl, uploadFileToS3, getProProfile, getMyConversations, ConversationSummary, blockUser, getWsToken } from "../api";
import ReportModal from "../components/ReportModal";
import { playNotificationSound } from "../utils/notificationSound";
import { formatTime, formatDate } from "../utils/dateFormat";
import SecureImage from "../components/SecureImage";

interface Message {
  conversationId: string;
  sentAt: string;
  message: string;
  senderId: string;
  recipientId: string;
  fileUrl?: string;
}

export default function MessagesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const conversationId = searchParams.get("conversation") || "";
  const recipientId = searchParams.get("recipient") || "";
  const recipientName = searchParams.get("name") || "Professional";
  // No conversation selected → show the inbox ("inbox" kept for old links).
  const isInbox = !conversationId || conversationId === "inbox";

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [profile, setProfile] = useState<any>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getProProfile().then(p => {
      setProfile(p);
      setTemplates(p?.messageTemplates || []);
    }).catch(() => {});
  }, []);
  const [userId, setUserId] = useState("");
  const [wsToken, setWsToken] = useState("");
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const initials = recipientName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  const wsAttemptsRef = useRef(0);
  const [wsRetry, setWsRetry] = useState(0);

  useEffect(() => {
    getWsToken().then(token => {
      if (token && token.split(".").length === 3) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          setUserId(payload.sub || "");
          setWsToken(token);
        } catch {}
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isInbox) return;
    getMyConversations()
      .then(setConversations)
      .catch(err => console.error("Failed to load conversations:", err))
      .finally(() => setInboxLoading(false));
  }, [isInbox]);

  useEffect(() => {
    if (!userId || !wsToken || !conversationId || isInbox) return;

    async function loadMessages() {
      try {
        const headers = await getAuthHeader();
        const response = await axios.get(
          `${awsConfig.apiUrl}/messages?conversationId=${conversationId}`,
          { headers }
        );
        setMessages(response.data.messages || []);
      } catch (err) { console.error("Failed to load messages:", err); }
      finally { setLoading(false); }
    }
    loadMessages();

    let disposed = false;
    const ws = new WebSocket(`${awsConfig.wsUrl}?token=${encodeURIComponent(wsToken)}`);
    wsRef.current = ws;
    ws.onopen = () => { wsAttemptsRef.current = 0; setWsConnected(true); };
    ws.onclose = () => {
      setWsConnected(false);
      // Reconnect with a freshly refreshed token — ID tokens expire after
      // 1h, so a long-lived tab needs a new one to reopen the socket.
      if (!disposed && wsAttemptsRef.current < 5) {
        wsAttemptsRef.current += 1;
        setTimeout(() => {
          if (disposed) return;
          getWsToken()
            .then(t => { if (t) setWsToken(t); setWsRetry(r => r + 1); })
            .catch(() => {});
        }, 3000);
      }
    };
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.conversationId === conversationId) {
        setMessages(prev => [...prev, data]);
        if (data.senderId !== userId) {
          playNotificationSound();
        }
      }
    };
    ws.onerror = (err) => console.error("WebSocket error:", err);
    return () => { disposed = true; ws.close(); };
  }, [userId, wsToken, conversationId, isInbox, wsRetry]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function sendMessage() {
    if (!newMessage.trim() && !attachmentFile || !wsRef.current) return;
    const ws = wsRef.current;
    if (ws.readyState !== WebSocket.OPEN) return;
    setSending(true);
    try {
      let fileUrl = "";
      if (attachmentFile) {
        const { uploadUrl, fileUrl: url } = await getUploadUrl(attachmentFile.name, attachmentFile.type, "messages");
        await uploadFileToS3(uploadUrl, attachmentFile);
        fileUrl = url;
      }
      const msgText = newMessage.trim() || (attachmentFile ? `📎 ${attachmentFile.name}` : "");
      ws.send(JSON.stringify({
        action: "sendMessage",
        conversationId,
        message: msgText,
        fileUrl,
        senderId: userId,
        recipientId
      }));
      setMessages(prev => [...prev, {
        conversationId,
        sentAt: new Date().toISOString(),
        message: msgText,
        fileUrl,
        senderId: userId,
        recipientId
      }]);
      setNewMessage("");
      setAttachmentFile(null);
      setAttachmentPreview(null);
    } finally { setSending(false); }
  }

  // Group messages by date
  function shouldShowDate(messages: Message[], index: number) {
    if (index === 0) return true;
    const curr = new Date(messages[index].sentAt).toDateString();
    const prev = new Date(messages[index - 1].sentAt).toDateString();
    return curr !== prev;
  }

  if (isInbox) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="bg-white px-5 pt-5 pb-4 border-b border-gray-100 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)}
              className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors flex-shrink-0">
              ←
            </button>
            <div className="font-bold text-navy text-lg">Messages</div>
          </div>
        </div>

        <div className="flex-1 px-4 py-4">
          {inboxLoading && (
            <div className="space-y-3 py-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="skeleton h-16 rounded-2xl"></div>
              ))}
            </div>
          )}

          {!inboxLoading && conversations.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 animate-fade-in">
              <div className="w-16 h-16 bg-blue-pale rounded-full flex items-center justify-center text-3xl mb-4">💬</div>
              <div className="font-bold text-navy mb-1">No messages yet</div>
              <div className="text-xs text-gray-400 text-center max-w-xs">
                Conversations with your leads and bookings will appear here
              </div>
            </div>
          )}

          {!inboxLoading && conversations.map(conv => {
            const convInitials = conv.otherUserName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
            const isToday = new Date(conv.lastSentAt).toDateString() === new Date().toDateString();
            return (
              <button key={conv.conversationId}
                onClick={() => navigate(`/messages?conversation=${conv.conversationId}&recipient=${conv.otherUserId}&name=${encodeURIComponent(conv.otherUserName)}`)}
                className="w-full bg-white rounded-2xl shadow-card p-4 mb-3 flex items-center gap-3 text-left hover:bg-blue-pale transition-colors">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-mouna-blue to-navy flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                  {convInitials || "👤"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-navy text-sm truncate">{conv.otherUserName}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {conv.lastSenderId === userId ? "You: " : ""}{conv.lastMessage}
                  </div>
                </div>
                <div className="text-xs text-gray-400 flex-shrink-0">
                  {isToday ? formatTime(conv.lastSentAt) : formatDate(conv.lastSentAt)}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white px-5 pt-5 pb-4 border-b border-gray-100 sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors flex-shrink-0">
            ←
          </button>
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-mouna-blue to-navy flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
            {initials || "👤"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-navy text-sm truncate">{recipientName}</div>
            <div className="flex items-center gap-1.5">
              <div className={"w-2 h-2 rounded-full " + (wsConnected ? "bg-teal" : "bg-gray-300")}></div>
              <span className={"text-xs " + (wsConnected ? "text-teal" : "text-gray-400")}>
                {wsConnected ? "Connected" : "Connecting..."}
              </span>
            </div>
          </div>
          {recipientId && (
            <div className="relative">
              <button onClick={() => setShowMenu(!showMenu)}
                className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center text-lg hover:bg-gray-200 transition-colors">
                ⋯
              </button>
              {showMenu && (
                <div className="absolute right-0 top-11 bg-white rounded-2xl shadow-card border border-gray-100 py-2 w-44 z-30">
                  <button onClick={() => { setShowMenu(false); setShowReport(true); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-navy hover:bg-gray-50">
                    🚩 Report {recipientName}
                  </button>
                  <button onClick={() => { setShowMenu(false); setShowBlockConfirm(true); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-50">
                    🚫 Block {recipientName}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4" style={{paddingBottom: "80px"}}>
        {loading && (
          <div className="space-y-4 py-4">
            {[1,2,3].map(i => (
              <div key={i} className={"flex " + (i % 2 === 0 ? "justify-end" : "justify-start")}>
                <div className="skeleton h-10 w-48 rounded-2xl"></div>
              </div>
            ))}
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full py-20 animate-fade-in">
            <div className="w-16 h-16 bg-blue-pale rounded-full flex items-center justify-center text-3xl mb-4">💬</div>
            <div className="font-bold text-navy mb-1">Start the conversation</div>
            <div className="text-xs text-gray-400 text-center max-w-xs">
              Send a message to {recipientName} to discuss your project
            </div>
          </div>
        )}

        {messages.map((msg, i) => {
          const isMe = msg.senderId === userId;
          const showDate = shouldShowDate(messages, i);
          const showAvatar = !isMe && (i === 0 || messages[i - 1].senderId === userId);

          return (
            <div key={i}>
              {showDate && (
                <div className="flex justify-center my-4">
                  <span className="bg-gray-100 text-gray-400 text-xs rounded-full px-3 py-1">
                    {new Date(msg.sentAt).toDateString() === new Date().toDateString()
                      ? "Today" : formatDate(msg.sentAt)}
                  </span>
                </div>
              )}
              <div className={"flex mb-1.5 " + (isMe ? "justify-end" : "justify-start")}>
                {!isMe && (
                  <div className={"w-8 h-8 rounded-full bg-navy flex items-center justify-center text-white text-xs font-bold mr-2 flex-shrink-0 self-end " +
                    (showAvatar ? "opacity-100" : "opacity-0")}>
                    {initials || "P"}
                  </div>
                )}
                <div className={"max-w-xs lg:max-w-md " + (isMe ? "items-end" : "items-start") + " flex flex-col"}>
                  <div className={"px-4 py-2.5 rounded-2xl text-sm leading-relaxed " +
                    (isMe
                      ? "bg-mouna-blue text-white rounded-br-sm"
                      : "bg-white text-navy rounded-bl-sm shadow-card")}>
                    {msg.fileUrl && (
                      <SecureImage src={msg.fileUrl} alt="Attachment" className="rounded-xl max-w-[220px] max-h-[220px] object-cover mb-1"/>
                    )}
                    {msg.message}
                  </div>
                  <div className={"text-xs mt-1 px-1 " + (isMe ? "text-right text-gray-400" : "text-gray-400")}>
                    {formatTime(msg.sentAt)}
                    {isMe && <span className="ml-1 text-mouna-blue">✓✓</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>

      {/* Attachment preview */}
      {attachmentPreview && (
        <div className="fixed bottom-20 left-4 right-4 z-30">
          <div className="bg-white rounded-2xl shadow-card p-2 flex items-center gap-3">
            <img src={attachmentPreview} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold text-navy truncate">{attachmentFile?.name}</div>
              <div className="text-xs text-gray-400">{attachmentFile ? (attachmentFile.size / 1024).toFixed(0) + " KB" : ""}</div>
            </div>
            <button onClick={() => { setAttachmentFile(null); setAttachmentPreview(null); }}
              className="w-7 h-7 bg-red-50 text-red-400 rounded-full flex items-center justify-center text-xs flex-shrink-0">✕</button>
          </div>
        </div>
      )}
      {attachmentFile && !attachmentPreview && (
        <div className="fixed bottom-20 left-4 right-4 z-30">
          <div className="bg-white rounded-2xl shadow-card p-3 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-pale rounded-xl flex items-center justify-center text-xl flex-shrink-0">📄</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold text-navy truncate">{attachmentFile?.name}</div>
              <div className="text-xs text-gray-400">{(attachmentFile.size / 1024).toFixed(0)} KB</div>
            </div>
            <button onClick={() => { setAttachmentFile(null); setAttachmentPreview(null); }}
              className="w-7 h-7 bg-red-50 text-red-400 rounded-full flex items-center justify-center text-xs flex-shrink-0">✕</button>
          </div>
        </div>
      )}
      {/* Input */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-3 z-30">
        <div className="flex items-center gap-2">
          <div className="relative flex-shrink-0">
            <input ref={fileInputRef} type="file" accept="image/*,video/*,application/pdf" className="hidden"
              onChange={e => {
                const file = e.target.files?.[0];
                if (!file) return;
                setAttachmentFile(file);
                if (file.type.startsWith("image/")) {
                  const reader = new FileReader();
                  reader.onload = ev => setAttachmentPreview(ev.target?.result as string);
                  reader.readAsDataURL(file);
                } else {
                  setAttachmentPreview(null);
                }
              }}/>
            <button onClick={() => fileInputRef.current?.click()}
              className={"w-10 h-10 rounded-full flex items-center justify-center text-xl transition-colors flex-shrink-0 " +
                (attachmentFile ? "bg-mouna-blue text-white" : "bg-gray-100 hover:bg-gray-200")}>
              📎
            </button>
          </div>
          {templates.length > 0 && (
            <button onClick={() => setShowTemplates(true)}
              className="w-10 h-10 bg-blue-pale text-mouna-blue rounded-full flex items-center justify-center text-base flex-shrink-0 hover:bg-mouna-blue hover:text-white transition-colors flex-shrink-0"
              title="Use Template">
              📝
            </button>
          )}
          <div className="flex-1 relative">
            <input ref={inputRef} type="text"
              placeholder="Type a message..."
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              onKeyPress={e => e.key === "Enter" && sendMessage()}
              className="w-full bg-gray-100 rounded-full px-4 py-2.5 text-sm outline-none focus:bg-white focus:ring-2 focus:ring-mouna-blue transition-all pr-4"/>
          </div>
          <button onClick={sendMessage} disabled={!newMessage.trim() || sending}
            className={"w-10 h-10 rounded-full flex items-center justify-center text-white font-bold transition-all flex-shrink-0 " +
              (newMessage.trim() ? "bg-mouna-blue hover:bg-blue-700 shadow-button" : "bg-gray-200")}>
            {sending ? "..." : "↑"}
          </button>
        </div>
      </div>

      {showReport && (
        <ReportModal targetType="user" targetId={recipientId} targetName={recipientName}
          onClose={() => setShowReport(false)}/>
      )}

      {showBlockConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowBlockConfirm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5" onClick={e => e.stopPropagation()}>
            <h2 className="font-bold text-navy mb-2">Block {recipientName}?</h2>
            <p className="text-sm text-gray-500 mb-5">
              They won't be able to message you, and this conversation will disappear from your inbox. You can unblock them later by contacting support.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowBlockConfirm(false)}
                className="flex-1 bg-gray-100 text-navy rounded-2xl py-3 font-bold text-sm">
                Cancel
              </button>
              <button disabled={blocking}
                onClick={async () => {
                  setBlocking(true);
                  try {
                    await blockUser(recipientId, "block");
                    navigate("/messages");
                  } catch {
                    setBlocking(false);
                    setShowBlockConfirm(false);
                  }
                }}
                className="flex-1 bg-red-500 text-white rounded-2xl py-3 font-bold text-sm">
                {blocking ? "Blocking..." : "Block"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTemplates && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowTemplates(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[70vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="font-bold text-navy">Choose a Template</h2>
              <button onClick={() => setShowTemplates(false)} className="text-gray-400 text-2xl">×</button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {templates.map((tpl: any) => (
                <button key={tpl.id} onClick={() => {
                  const filled = tpl.body
                    .replace(/\{client_name\}/g, "there")
                    .replace(/\{service_type\}/g, "your service")
                    .replace(/\{job_date\}/g, "soon")
                    .replace(/\{company_name\}/g, profile?.businessName || profile?.name || "")
                    .replace(/\{your_name\}/g, profile?.name || "")
                    .replace(/\{phone\}/g, profile?.phone || "");
                  setNewMessage(filled);
                  setShowTemplates(false);
                }} className="w-full bg-gray-50 hover:bg-blue-pale rounded-2xl p-3 text-left transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold rounded-full px-2 py-0.5 bg-white text-mouna-blue">
                      {tpl.type === "quote" ? "💰 Quote" : "💬 Message"}
                    </span>
                    <div className="font-bold text-navy text-sm">{tpl.name}</div>
                  </div>
                  <div className="text-xs text-gray-500 line-clamp-2">{tpl.body.slice(0, 120)}...</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
